import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../redux/store';
import { refreshStalePlan } from '../../features/client';
import { isStalePlanError } from '../../utils';

const useAppDispatch = () => useDispatch<AppDispatch>();

// AEX-3363: shared stale-plan handling for the save flows that can hit a reference-ID mismatch
// (the Flow2 inline Profile/Accounts/Goals tables and the console Reallocate modal).
// - markStaleFromError: call inside a save catch. Returns true when the error was a stale-plan
//   (reference-ID mismatch) error, in which case the caller should skip its generic error banner.
// - refreshPlan: re-fetch the latest plan; on success clears the banner and runs `onRefreshed`
//   (e.g. to close the open edit row that the failed save left behind).
export const useStalePlanRefresh = (
    onRefreshed?: () => void,
    // eslint-disable-next-line no-unused-vars
    controlled?: { isStalePlan: boolean; setIsStalePlan: (value: boolean) => void }
) => {
    const dispatch = useAppDispatch();
    const [localIsStalePlan, setLocalIsStalePlan] = useState(false);
    const isStalePlan = controlled ? controlled.isStalePlan : localIsStalePlan;
    const setIsStalePlan = controlled ? controlled.setIsStalePlan : setLocalIsStalePlan;

    const markStaleFromError = useCallback(
        (error: unknown): boolean => {
            if (isStalePlanError(error)) {
                setIsStalePlan(true);
                return true;
            }
            return false;
        },
        [setIsStalePlan]
    );

    const refreshPlan = useCallback(async () => {
        try {
            await dispatch(refreshStalePlan()).unwrap();
            setIsStalePlan(false);
            onRefreshed?.();
        } catch {
            // Refresh failed — keep the banner so the advisor can retry.
        }
    }, [dispatch, onRefreshed, setIsStalePlan]);

    return { isStalePlan, markStaleFromError, refreshPlan };
};
